/**
 * Bouton WhatsApp « Envoyez votre configuration » (toutes pages produit).
 *
 * Au clic, construit le message à partir de l'état réel du formulaire
 * produit : options de la variante sélectionnée (via la carte JSON
 * [data-whatsapp-variants]), propriétés du configurateur si présent,
 * quantité et prix, puis met à jour le href wa.me avant la navigation.
 * Le lien reste fonctionnel sans ce script (href de repli côté serveur).
 *
 * Aucune dépendance : ne touche ni au configurateur ni au panier.
 */
(function () {
  'use strict';

  /** Prix en centimes → « 1 234,56 € » (repli brut si Intl indisponible). */
  function formatPrice(cents, currency) {
    var amount = cents / 100;
    try {
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currency }).format(amount);
    } catch (e) {
      return amount.toFixed(2) + ' ' + currency;
    }
  }

  /**
   * Idem, arrondi à l'euro supérieur et sans décimales : sur un montant
   * restant à atteindre, « 311 € » se lit mieux que « 311,00 € ».
   */
  function formatPriceRounded(cents, currency) {
    var euros = Math.ceil(cents / 100);
    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(euros);
    } catch (e) {
      return euros + ' ' + currency;
    }
  }

  /**
   * Variante sélectionnée : lue au clic depuis l'input caché name="id" du
   * formulaire, cherchée dans la carte JSON rendue par le snippet.
   * Renvoie null si la carte ou la variante est introuvable.
   */
  function readVariant(cta, form) {
    // Même repli que findForm : les CTA persistants n'ont pas d'id de formulaire.
    var mapEl =
      (cta.dataset.formId &&
        document.querySelector(
          '[data-whatsapp-variants][data-form-id="' + cta.dataset.formId + '"]'
        )) ||
      document.querySelector('[data-whatsapp-variants]');
    if (!mapEl) return null;
    try {
      var map = JSON.parse(mapEl.textContent);
      var idInput = form.querySelector('input[name="id"]');
      var variant = idInput && map.variants[idInput.value];
      if (!variant) return null;
      return {
        id: idInput.value,
        price: variant.price,
        // Photo propre a la variante quand elle en a une (repli d'image du message).
        image: variant.image || '',
        // Paires « Nom d'option : valeur », omises pour la variante par défaut.
        options: map.hasOnlyDefaultVariant
          ? []
          : map.optionNames.map(function (name, i) {
              return { label: name, value: variant.options[i] };
            })
      };
    } catch (e) {
      return null;
    }
  }

  /** « + 49.90 € », « 1 234,56 € » ou « 1,234.56 € » → centimes (0 si non
   *  analysable). Les deux conventions de milliers arrivent ici : l'app
   *  d'options écrit ses prix en format anglais alors que le thème affiche
   *  en français. Ne traiter que la convention française amputait le total
   *  de 1 000 € dès que la configuration les dépassait. */
  function parsePriceCents(text) {
    var raw = (text || '').replace(/[^\d.,]/g, '');
    if (!raw) return 0;
    if (raw.indexOf('.') > -1 && raw.indexOf(',') > -1) {
      // Le dernier séparateur est le décimal, l'autre marque les milliers.
      raw = raw.lastIndexOf('.') > raw.lastIndexOf(',')
        ? raw.replace(/,/g, '')
        : raw.replace(/\./g, '').replace(',', '.');
    } else if (raw.indexOf(',') > -1) {
      raw = /,\d{3}(\D|$)/.test(raw) ? raw.replace(/,/g, '') : raw.replace(',', '.');
    }
    var value = parseFloat(raw);
    return isNaN(value) ? 0 : Math.round(value * 100);
  }

  /**
   * Propriétés visibles du formulaire (configurateur, app d'options, champs
   * personnalisés) : radios cochées, selects et champs texte nommés
   * properties[...]. Les propriétés machine (préfixe _) et les valeurs vides
   * sont ignorées.
   *
   * L'app d'options écrit le supplément payant dans la valeur même de la
   * propriété (« Carbone + alcantara | 149.90 € ») — c'est ce qui arrive tel
   * quel dans le panier. On extrait ce montant dans `cents` pour le total.
   */
  function readProperties(form) {
    var options = [];
    var seen = {};
    for (var i = 0; i < form.elements.length; i++) {
      var el = form.elements[i];
      var match = el.name && el.name.match(/^properties\[(.+)\]$/);
      if (!match) continue;
      var rawLabel = match[1];
      if (rawLabel.charAt(0) === '_') continue; // propriété machine (_config, _hash)
      if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) continue;
      // La deduplication porte sur le nom brut : deux champs distincts le
      // restent meme si leur libelle nettoye devient identique.
      if (!el.value || seen[rawLabel]) continue;
      seen[rawLabel] = true;
      var priceSuffix = el.value.match(/\|\s*([\d\s.,]+\s*[€$£])\s*$/);
      options.push({
        label: cleanLabel(rawLabel),
        value: el.value,
        cents: priceSuffix ? parsePriceCents(priceSuffix[1]) : 0
      });
    }
    return options;
  }

  /**
   * Retire l'identifiant interne que l'app d'options YMQ accole au nom de
   * certaines proprietes : « 10. Ajouter un couvercle d'airbag personnalise-28-6 ».
   *
   * Le motif est toujours deux groupes de chiffres separes par des tirets, en
   * fin de nom (-1-17, -28-6, -29-20, -28-107...). Verifie sur 30 commandes
   * reelles : la numerotation metier des etapes est en points (4.1.1, 11.5,
   * « 13. 1 ») et aucun libelle legitime ne se termine par -chiffres-chiffres.
   * Le nettoyage est donc sans risque pour les intitules vus par le client.
   */
  function cleanLabel(label) {
    return label.replace(/-\d+-\d+$/, '').trim();
  }

  function buildMessage(cta, form) {
    // Pas d'emoji ici : certains appareils les corrompent via le lien wa.me.
    var lines = ['Bonjour Les As du Volant,', '', 'Je souhaite vous envoyer ma configuration :', ''];

    // Nom du produit
    var title = cta.dataset.productTitle;
    if (title) lines.push('Produit : ' + title);

    // Options de la variante sélectionnée, puis propriétés (configurateur…)
    var variant = readVariant(cta, form);
    var properties = readProperties(form);
    var selections = (variant ? variant.options : []).concat(properties);
    selections.forEach(function (opt) {
      if (opt.value) lines.push('• ' + opt.label + ' : ' + opt.value);
    });

    // Quantité (défaut 1 si le champ est absent ou vide)
    var qtyInput = form.elements.namedItem('quantity');
    var qty = qtyInput && parseInt(qtyInput.value, 10) > 0 ? parseInt(qtyInput.value, 10) : 1;
    lines.push('Quantité : ' + qty);

    // Identifiant de la variante : l'automatisation des devis (Make) s'en sert
    // pour créer le brouillon de commande Shopify sur le bon article.
    if (variant && variant.id) lines.push('Code article : ' + variant.id);

    // Prix total = (prix de la variante courante + suppléments des options
    // payantes) × quantité (repli : prix rendu côté serveur ; ligne omise
    // si aucun prix disponible)
    var unitCents = variant ? variant.price : parseInt(cta.dataset.priceCents, 10);
    var totalCents = 0;
    if (unitCents > 0) {
      properties.forEach(function (opt) { unitCents += opt.cents; });
      totalCents = unitCents * qty;
      lines.push('Total : ' + formatPrice(totalCents, cta.dataset.currency || 'EUR'));
    }

    // Offre en cours : 90 % des commandes passent par WhatsApp et ne voient
    // donc jamais la barre de progression du panier. On porte l'information
    // dans le message lui-même, en la personnalisant selon le total réel :
    // le client ouvre la conversation en sachant s'il est éligible ou ce qui
    // lui manque — ce qui ouvre naturellement la discussion sur les options.
    var promo = window.PROMO_ETE150;
    if (promo && totalCents > 0 && promo.threshold) {
      var deadlineOk = !promo.deadline || new Date(promo.deadline).getTime() > Date.now();
      if (deadlineOk) {
        var missing = promo.threshold - totalCents;
        lines.push(
          '',
          missing <= 0
            ? 'Offre : ma configuration atteint le seuil des 150 € offerts.'
            : 'Offre : il me manque ' +
              formatPriceRounded(missing, cta.dataset.currency || 'EUR') +
              ' pour les 150 € offerts.'
        );
      }
    }

    // URL du produit + configuration partageable (?cfg= maintenu par le configurateur)
    var url = cta.dataset.productUrl || '';
    if (url) {
      var cfg = new URLSearchParams(window.location.search).get('cfg');
      if (cfg) url += '?cfg=' + encodeURIComponent(cfg);
      lines.push('', 'Lien : ' + url);
    }

    // Image de référence en fin de message, TOUJOURS sous le même libellé :
    // elle sert de base à la génération IA du visuel renvoyé au client, et un
    // libellé unique permet de la retrouver sans distinguer les cas. Forme
    // cochée quand la fiche pose la question, sinon photo héros de la fiche.
    var refImage = readShapeImage(form) || readProductImage(cta, variant);
    if (refImage) lines.push('', 'Image de la forme : ' + refImage);

    return lines.join('\n');
  }

  /**
   * URL de l'image de la forme cochée dans l'app d'options YMQ.
   *
   * La question existe sous plusieurs blocs conditionnels (« 2. Forme du
   * Volant-1-17 », « -1-35 »… selon le modèle choisi en question 1) : seul
   * le bloc visible compte, les blocs masqués gardent une valeur cochée par
   * défaut. L'image est posée en background de l'étiquette du choix ; on
   * retire le redimensionnement (&width=200&height=200) pour envoyer
   * l'image en taille réelle. « Forme des palettes » n'est pas concernée.
   */
  function readShapeImage(form) {
    var scope = form.querySelector('.ymq-options-box') ? form : document;
    var boxes = scope.querySelectorAll('.ymq-options-box[data-label]');
    for (var i = 0; i < boxes.length; i++) {
      var box = boxes[i];
      if (!/forme du volant/i.test(box.dataset.label) || box.offsetParent === null) continue;
      var input = box.querySelector('input:checked');
      if (!input) continue;
      var label = box.querySelector('label[for="' + input.id + '"]');
      var src = '';
      var bg = label && label.style.backgroundImage.match(/url\(["']?([^"')]+)["']?\)/);
      if (bg) {
        src = bg[1];
      } else {
        var img = label && label.querySelector('img');
        src = img ? img.getAttribute('src') : '';
      }
      if (!src) continue;
      return toAbsolute(src.replace(/[?&](width|height)=\d+/g, '').replace(/^([^?]*)&/, '$1?'));
    }
    return '';
  }

  /**
   * Repli quand la fiche ne pose pas la question du modèle (donc pas celle
   * de la forme), ou qu'aucune forme n'est encore cochée : photo héros de la
   * fiche sur le CDN Shopify — celle de la variante sélectionnée si elle en a
   * une, sinon l'image principale du produit, rendue en attribut par le
   * snippet du CTA.
   */
  function readProductImage(cta, variant) {
    var src = (variant && variant.image) || cta.dataset.productImage || '';
    return src ? toAbsolute(src) : '';
  }

  /** URL CDN sans protocole (« //cdn.shopify.com/… ») vers https. */
  function toAbsolute(src) {
    return src.indexOf('//') === 0 ? 'https:' + src : src;
  }

  /**
   * Formulaire produit associé au CTA.
   *
   * Le CTA rendu dans buy-buttons connaît l'id du <form> ; les CTA persistants
   * (barre collante, bouton flottant) sont rendus hors de la section produit et
   * ne l'ont pas. On retombe alors sur le premier formulaire d'ajout au panier
   * de la page, qui est celui du produit affiché.
   */
  function findForm(cta) {
    var byId = cta.dataset.formId && document.getElementById(cta.dataset.formId);
    if (byId) return byId;

    // Dawn rend deux formulaires /cart/add sur une fiche produit : le
    // formulaire de paiement échelonné (caché, id product-form-installment-…,
    // sans propriétés) arrive EN PREMIER dans le DOM. Prendre le premier venu
    // produirait un message sans aucune option choisie — exactement ce que ce
    // bouton doit transmettre. On écarte donc l'échelonné et on privilégie un
    // formulaire visible, puis celui qui porte le plus de properties[].
    var forms = [].slice
      .call(document.querySelectorAll('form[action*="/cart/add"]'))
      .filter(function (f) {
        return (f.getAttribute('id') || '').indexOf('installment') === -1;
      });
    if (!forms.length) return null;

    var visible = forms.filter(function (f) {
      return f.offsetParent !== null;
    });
    var candidates = visible.length ? visible : forms;

    return candidates.reduce(function (best, f) {
      return countProperties(f) > countProperties(best) ? f : best;
    }, candidates[0]);
  }

  /** Nombre de champs properties[...] d'un formulaire. */
  function countProperties(form) {
    var n = 0;
    for (var i = 0; i < form.elements.length; i++) {
      if (/^properties\[/.test(form.elements[i].name || '')) n++;
    }
    return n;
  }

  document.addEventListener('click', function (evt) {
    var cta = evt.target.closest('[data-whatsapp-cta]');
    if (!cta) return;

    var form = findForm(cta);
    // Sans formulaire, le href de repli (produit + URL) est conservé.
    if (!form) return;

    cta.href = 'https://wa.me/' + cta.dataset.phone + '?text=' + encodeURIComponent(buildMessage(cta, form));
  });
})();
